import { useState, useRef, useEffect, useCallback } from "react";
import { Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchProducts, type ShopifyProduct } from "@/lib/shopify";
import { useCurrency } from "@/contexts/CurrencyContext";
import { shopifyImageUrl, shopifyImageSrcSet } from "@/lib/shopify-image";

interface HeaderSearchProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const HeaderSearch = ({ isOpen, onOpen, onClose }: HeaderSearchProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ShopifyProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimating(true));
      });
      setTimeout(() => inputRef.current?.focus(), 350);
    } else {
      setAnimating(false);
      const timer = setTimeout(() => {
        setVisible(false);
        setQuery("");
        setResults([]);
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const products = await fetchProducts(8, q);
      setResults(products);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (val: string) => {
    setQuery(val);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => search(val), 350);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") onClose();
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-background/80 backdrop-blur-sm transition-opacity duration-300 ${
          animating ? "opacity-100" : "opacity-0"
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(0.16, 1, 0.30, 1)" }}
        onClick={onClose}
      />

      {/* Search bar — slides down from top */}
      <div
        className="fixed top-0 left-0 right-0 z-[70] bg-background border-b border-border transition-transform duration-[350ms]"
        style={{
          transform: animating ? "translateY(0)" : "translateY(-100%)",
          transitionTimingFunction: "cubic-bezier(0.16, 1, 0.30, 1)",
        }}
      >
        <div
          className="mx-auto w-full flex items-center gap-4 px-5 md:px-8 lg:px-10"
          style={{ height: "var(--header-height)" }}
        >
          <Search size={20} strokeWidth={1.4} className="text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search watches, jewelry..."
            className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground outline-none body-medium"
          />
          <button onClick={onClose} className="text-foreground hover:text-accent transition-colors" aria-label="Close search">
            <X size={20} strokeWidth={1.4} />
          </button>
        </div>

        {/* Results dropdown */}
        {(results.length > 0 || loading) && (
          <div className="border-t border-border max-h-[70vh] overflow-y-auto">
            {loading && (
              <div className="px-5 md:px-8 lg:px-10 py-6">
                <p className="body-small text-muted-foreground">Searching...</p>
              </div>
            )}
            {!loading && results.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
                {results.map((product) => {
                  const img = product.images.edges[0]?.node;
                  const price = formatPrice(
                    product.priceRange.minVariantPrice.amount,
                    product.priceRange.minVariantPrice.currencyCode
                  );
                  return (
                    <Link
                      key={product.id}
                      to={`/product/${product.handle}`}
                      onClick={onClose}
                      className="bg-background p-4 flex gap-3 items-center hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-16 h-16 flex-shrink-0 overflow-hidden bg-muted">
                        {img && (
                          <img
                            src={shopifyImageUrl(img.url, { width: 128 }) || img.url}
                            srcSet={shopifyImageSrcSet(img.url, [96, 128, 192]) || undefined}
                            sizes="64px"
                            alt={img.altText || product.title}
                            width={64}
                            height={64}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="body-small text-foreground truncate">{product.title}</p>
                        <p className="body-small text-muted-foreground">{price}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default HeaderSearch;
